import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileSpreadsheet, Search, Users, Crown, UserX, Shield, RefreshCw, Trash2,
  X, Loader2, AlertTriangle, CheckCircle2, XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const API_URL = process.env.REACT_APP_API_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('tr-TR'); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('tr-TR'); } catch { return iso; }
};

const PLAN_LABEL = { free: 'Free', pro: 'Pro', business: 'Business' };
const PLAN_COLOR = {
  free: 'bg-slate-100 text-slate-700',
  pro: 'bg-gradient-to-r from-indigo-100 to-fuchsia-100 text-indigo-800',
  business: 'bg-amber-100 text-amber-800',
};

// YYYY-MM-DD, `days` from today (local date).
const isoInDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * QuoteUsersPanel — SuperAdmin: Teklif Kullanıcıları.
 * Sadece business.product == "quote_builder" hesapları için CRUD-lite panel.
 * Detay bölmesinde Pro yetkisi ver + Hesabı sil işlemleri yer alır.
 */
export const QuoteUsersPanel = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [setupFilter, setSetupFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null); // full user detail
  const [detailLoading, setDetailLoading] = useState(false);

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantDuration, setGrantDuration] = useState('30d');
  const [grantPlan, setGrantPlan] = useState('pro');
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [grantCustom, setGrantCustom] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/quote-users/summary`, { withCredentials: true });
      setSummary(data);
    } catch {
      setSummary(null);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (qDebounced) params.q = qDebounced;
      if (planFilter !== 'all') params.plan = planFilter;
      if (setupFilter !== 'all') params.setup = setupFilter;
      const { data } = await axios.get(`${API_URL}/api/superadmin/quote-users`, {
        params, withCredentials: true,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Teklif kullanıcıları yüklenemedi');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, qDebounced, planFilter, setupFilter]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setSelected({ id: userId, loading: true });
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/quote-users/${userId}`, { withCredentials: true });
      setSelected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kullanıcı yüklenemedi');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setGrantOpen(false); setGrantDuration('30d'); setGrantCustom(''); setGrantPlan('pro');
    setDeleteOpen(false); setDeleteEmail('');
  };

  const reloadSelected = async () => {
    const { data } = await axios.get(`${API_URL}/api/superadmin/quote-users/${selected.id}`, { withCredentials: true });
    setSelected(data);
    fetchList(); fetchSummary();
  };

  // Manual plans are an admin "override": they never touch payment records
  // and can be switched off again with one click.
  const submitGrant = async () => {
    if (!selected?.id) return;
    if (grantDuration === 'custom' && !grantCustom) {
      toast.error('Özel bitiş tarihi seçin.');
      return;
    }
    const expiresAt = {
      '30d': isoInDays(30), '1y': isoInDays(365), custom: grantCustom, forever: null,
    }[grantDuration];
    setGrantBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/superadmin/quote-users/${selected.id}/grant-plan`,
        { plan: grantPlan, expires_at: expiresAt },
        { withCredentials: true },
      );
      await reloadSelected();
      toast.success(`${PLAN_LABEL[grantPlan]} yetkisi verildi.`);
      setGrantOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || 'Plan verilemedi');
    } finally {
      setGrantBusy(false);
    }
  };

  const revokeGrant = async () => {
    if (!selected?.id) return;
    if (!window.confirm('Manuel verilen plan kapatılsın mı? Ödemeyle alınmış bir plan varsa o devam eder.')) return;
    setRevokeBusy(true);
    try {
      await axios.delete(`${API_URL}/api/superadmin/quote-users/${selected.id}/plan-override`, { withCredentials: true });
      await reloadSelected();
      toast.success('Manuel plan kapatıldı.');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || 'Plan kapatılamadı');
    } finally {
      setRevokeBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!selected?.id || !selected?.email) return;
    if (deleteEmail.trim().toLowerCase() !== (selected.email || '').toLowerCase()) {
      toast.error('E-posta eşleşmedi. Silmek için hesabın e-postasını aynen yazın.');
      return;
    }
    setDeleteBusy(true);
    try {
      await axios.delete(`${API_URL}/api/superadmin/quote-users/${selected.id}`, {
        withCredentials: true,
        data: { email_confirm: deleteEmail.trim() },
      });
      toast.success('Hesap ve verileri silindi.');
      closeDetail();
      fetchList(); fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
        <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]" data-testid="sa-qu-title">
          Teklif Kullanıcıları
        </h1>
        <Button
          variant="outline"
          onClick={() => { fetchList(); fetchSummary(); }}
          className="gap-2"
          data-testid="sa-qu-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#595959] text-xs"><Users className="w-4 h-4" />Toplam</div>
          <p className="text-2xl font-bold text-[#1A1A2E] mt-1" data-testid="sa-qu-total">{summary?.total_users ?? '—'}</p>
        </CardContent></Card>
        <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#595959] text-xs"><Shield className="w-4 h-4" />Free</div>
          <p className="text-2xl font-bold text-slate-700 mt-1" data-testid="sa-qu-free">{summary?.free_users ?? '—'}</p>
        </CardContent></Card>
        <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#595959] text-xs"><Crown className="w-4 h-4" />Pro</div>
          <p className="text-2xl font-bold text-indigo-700 mt-1" data-testid="sa-qu-pro">{summary?.pro_users ?? '—'}</p>
          {summary?.business_users ? <p className="text-[11px] text-amber-700" data-testid="sa-qu-business">+ {summary.business_users} Business</p> : null}
        </CardContent></Card>
        <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#595959] text-xs"><FileSpreadsheet className="w-4 h-4" />Bu Ay Yeni</div>
          <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="sa-qu-new">{summary?.new_this_month ?? '—'}</p>
          <p className="text-[10px] text-[#94A3B8] mt-1">
            Toplam: {summary?.lifetime_quote_count ?? 0} teklif · {summary?.lifetime_ai_count ?? 0} AI
          </p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-3 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Arama</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Ad, e-posta veya firma adı"
              className="pl-8"
              data-testid="sa-qu-search"
            />
          </div>
        </div>
        <div className="w-full sm:w-40">
          <Label className="text-xs">Plan</Label>
          <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="sa-qu-plan-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="business">Business</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-52">
          <Label className="text-xs">Kurulum</Label>
          <Select value={setupFilter} onValueChange={(v) => { setSetupFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="sa-qu-setup-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="completed">Tamamlanan</SelectItem>
              <SelectItem value="pending">Tamamlanmamış</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-x-auto" data-testid="sa-qu-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ad / E-posta</TableHead>
              <TableHead>Firma</TableHead>
              <TableHead className="hidden md:table-cell">Sektör</TableHead>
              <TableHead className="hidden lg:table-cell">Kayıt</TableHead>
              <TableHead className="hidden lg:table-cell">Son Giriş</TableHead>
              <TableHead className="text-center">Plan</TableHead>
              <TableHead className="text-center hidden md:table-cell">Toplam Kullanım</TableHead>
              <TableHead className="text-center">Durum</TableHead>
              <TableHead className="text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#94A3B8]">Yükleniyor…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#94A3B8]">Kullanıcı bulunamadı</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow
                key={r.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => openDetail(r.id)}
                data-testid={`sa-qu-row-${r.id}`}
              >
                <TableCell>
                  <div className="font-medium text-[#1A1A2E]">{r.name || '—'}</div>
                  <div className="text-xs text-[#64748B]">{r.email}</div>
                </TableCell>
                <TableCell className="text-sm">{r.company_name || '—'}</TableCell>
                <TableCell className="text-sm hidden md:table-cell">{r.industry || '—'}</TableCell>
                <TableCell className="text-sm text-[#64748B] hidden lg:table-cell">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-sm text-[#64748B] hidden lg:table-cell">{fmtDate(r.last_login_at)}</TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLOR[r.plan]}`}>
                    {PLAN_LABEL[r.plan]}
                  </span>
                  {r.plan !== 'free' && r.plan_expires_at && (
                    <div className="text-[10px] text-[#94A3B8] mt-0.5">
                      {fmtDate(r.plan_expires_at)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-center text-xs hidden md:table-cell">
                  <span className="text-[#334155]">Teklif {r.total_quote_count ?? 0}</span>
                  <span className="text-[#94A3B8]"> / </span>
                  <span className="text-indigo-700">AI {r.total_ai_count ?? 0}</span>
                </TableCell>
                <TableCell className="text-center">
                  {r.is_frozen ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Donuk</span>
                  ) : r.setup_completed ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">Hazır</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">Kurulum</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost" size="sm"
                    onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}
                    data-testid={`sa-qu-open-${r.id}`}
                  >
                    Detay
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-xs text-[#64748B]">
        <span>Toplam: <b>{total}</b></span>
        <div className="flex gap-2 items-center">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="sa-qu-prev">Önceki</Button>
          <span>{page} / {pageCount}</span>
          <Button size="sm" variant="outline" disabled={page >= pageCount || loading}
            onClick={() => setPage((p) => p + 1)} data-testid="sa-qu-next">Sonraki</Button>
        </div>
      </div>

      {/* Detail slide-over */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={closeDetail} data-testid="sa-qu-detail-overlay" />
          <aside
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-50 shadow-xl overflow-y-auto"
            data-testid="sa-qu-detail-panel"
          >
            <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <p className="font-semibold text-[#1A1A2E]">Kullanıcı Detayı</p>
              <button onClick={closeDetail} className="p-1 hover:bg-slate-100 rounded" data-testid="sa-qu-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {detailLoading || selected?.loading ? (
              <div className="p-6 text-center text-[#94A3B8]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Yükleniyor…</div>
            ) : (
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-[#94A3B8]">Ad Soyad</p>
                  <p className="font-semibold" data-testid="sa-qu-d-name">{selected.name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#94A3B8]">E-posta</p>
                  <p className="font-medium" data-testid="sa-qu-d-email">{selected.email}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-[#94A3B8]">Firma</p>
                    <p className="text-sm">{selected.company_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Sektör</p>
                    <p className="text-sm">{selected.industry || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Ülke</p>
                    <p className="text-sm">{selected.country_code || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Kayıt Tarihi</p>
                    <p className="text-sm">{fmtDateTime(selected.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Son Giriş</p>
                    <p className="text-sm">{fmtDateTime(selected.last_login_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Kurulum</p>
                    <p className="text-sm flex items-center gap-1">
                      {selected.setup_completed
                        ? (<><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Tamamlandı</>)
                        : (<><XCircle className="w-4 h-4 text-amber-600" /> Tamamlanmadı</>)}
                    </p>
                  </div>
                </div>

                <div className="border-t border-[#E5E5E5] pt-3">
                  <p className="text-xs text-[#94A3B8]">Plan</p>
                  <p className="mt-1 flex items-center gap-2" data-testid="sa-qu-d-plan">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLOR[selected.plan]}`}>
                      {PLAN_LABEL[selected.plan]}
                    </span>
                    {selected.plan !== 'free' && (
                      <span className="text-xs text-[#64748B]">
                        {selected.plan_expires_at ? `Bitiş: ${fmtDate(selected.plan_expires_at)}` : 'Süresiz'}
                        {selected.plan_source ? ` · ${selected.plan_source === 'admin' ? 'manuel' : selected.plan_source}` : ''}
                      </span>
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] text-[#94A3B8]">Toplam Teklif</p>
                    <p className="font-bold text-[#1A1A2E]">{selected.total_quote_count ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] text-[#94A3B8]">Toplam AI</p>
                    <p className="font-bold text-indigo-700">{selected.total_ai_count ?? 0}</p>
                  </div>
                </div>

                {/* Grant Pro */}
                <div className="border-t border-[#E5E5E5] pt-3">
                  {!grantOpen ? (
                    <Button
                      onClick={() => setGrantOpen(true)}
                      className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-700 hover:to-fuchsia-700 text-white gap-2"
                      data-testid="sa-qu-grant-open"
                    >
                      <Crown className="w-4 h-4" /> Plan Ver (Pro / Business)
                    </Button>
                  ) : (
                    <div className="space-y-3 bg-indigo-50/50 border border-indigo-100 rounded-lg p-3">
                      <Label className="text-xs">Plan</Label>
                      <Select value={grantPlan} onValueChange={setGrantPlan}>
                        <SelectTrigger data-testid="sa-qu-grant-plan"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="business">Business (ekip, 5 kullanıcı)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Label className="text-xs">Süre</Label>
                      <Select value={grantDuration} onValueChange={setGrantDuration}>
                        <SelectTrigger data-testid="sa-qu-grant-duration"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30d">30 Gün</SelectItem>
                          <SelectItem value="1y">1 Yıl</SelectItem>
                          <SelectItem value="forever">Süresiz</SelectItem>
                          <SelectItem value="custom">Özel Bitiş Tarihi</SelectItem>
                        </SelectContent>
                      </Select>
                      {grantDuration === 'custom' && (
                        <div>
                          <Label className="text-xs">Bitiş Tarihi</Label>
                          <Input
                            type="date"
                            value={grantCustom}
                            onChange={(e) => setGrantCustom(e.target.value)}
                            data-testid="sa-qu-grant-custom"
                          />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button onClick={submitGrant} disabled={grantBusy}
                          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                          data-testid="sa-qu-grant-submit">
                          {grantBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                          Uygula
                        </Button>
                        <Button variant="outline" onClick={() => setGrantOpen(false)} data-testid="sa-qu-grant-cancel">İptal</Button>
                      </div>
                    </div>
                  )}
                </div>

                {(selected.plan_source === 'admin' || selected.plan_source === 'promo') && (
                  <Button
                    variant="outline"
                    onClick={revokeGrant}
                    disabled={revokeBusy}
                    className="w-full border-amber-300 text-amber-800 hover:bg-amber-50"
                    data-testid="sa-qu-revoke-plan"
                  >
                    {revokeBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Manuel Planı Kapat ({PLAN_LABEL[selected.plan]})
                  </Button>
                )}

                {/* Delete */}
                <div className="border-t border-[#E5E5E5] pt-3">
                  {!deleteOpen ? (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteOpen(true)}
                      className="w-full text-red-600 border-red-200 hover:bg-red-50 gap-2"
                      data-testid="sa-qu-delete-open"
                    >
                      <UserX className="w-4 h-4" /> Hesabı Sil
                    </Button>
                  ) : (
                    <div className="space-y-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                        <div className="text-xs text-red-800">
                          Bu işlem <b>geri alınamaz</b>. Kullanıcı, işletmesi, profili, müşterileri,
                          katalog kalemleri, teklifleri, kullanım ve AI logları silinecek. Finansal
                          işlem kayıtları saklanır, kişisel alanlar anonimleştirilir.
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Onaylamak için hesabın e-postasını yazın</Label>
                        <Input
                          value={deleteEmail}
                          onChange={(e) => setDeleteEmail(e.target.value)}
                          placeholder={selected.email}
                          data-testid="sa-qu-delete-email"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={submitDelete}
                          disabled={deleteBusy || (deleteEmail.trim().toLowerCase() !== (selected.email || '').toLowerCase())}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white gap-2"
                          data-testid="sa-qu-delete-submit"
                        >
                          {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          Kalıcı Olarak Sil
                        </Button>
                        <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteEmail(''); }} data-testid="sa-qu-delete-cancel">
                          İptal
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
};

export default QuoteUsersPanel;
